import { ChangeEvent, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUpload } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { pdsrUrls } from "../URL/ApplicationUrls";
import { combinedUrls } from "../URL/functions";

interface PdsrPreCheckResponse {
  new: number;
  updated: number;
  removed: number;
}

export default function PdsrCsvFileUploader() {
  const [selectedFile, setSelectedFile] = useState<File | null>();
  const [showPreCheckInfos, updateShowPreCheckInfos] = useState<boolean>(false);
  const [isFinished, updateIsFinished] = useState<boolean>(false);
  const [isSuccessful, updateIsSuccessful] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [preCheckResponse, updatePreCheckResponse] = useState<PdsrPreCheckResponse>();
  const uploadInput = useRef<HTMLInputElement>(null);

  const changeHandler = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setSelectedFile(event.target.files[0]);
      await handlePreCheck(event.target.files[0]);
    } else {
      handleReject();
    }
  };

  const handlePreCheck = async (file: File) => {
    const formData = new FormData();
    formData.append("fileUpload", file, file.name);
    const url = combinedUrls(pdsrUrls.upload, "/PreCheck");

    updateIsFinished(false);

    await axios
      .postForm(url, formData)
      .then((response) => {
        const { data } = response;

        updateShowPreCheckInfos(true);
        updatePreCheckResponse({
          updated: data.UpdatedElements.length,
          new: data.NewElements.length,
          removed: data.RemovedElements.length,
        });

        updateIsSuccessful(true);
      })
      .catch((error) => {
        updateIsSuccessful(false);
        updateIsFinished(true);
        setErrorMessage(error.response?.data?.title);
      });
  };

  const handleAccept = async () => {
    updateIsFinished(false);

    const formData = new FormData();
    if (selectedFile) {
      formData.append("fileUpload", selectedFile, selectedFile?.name);
    }

    const url = combinedUrls(pdsrUrls.upload, "/Upload");

    try {
      await axios.postForm(url, formData);
      updateIsSuccessful(true);
    } catch (error) {
      updateIsSuccessful(false);
    } finally {
      updateIsFinished(true);
    }
  };

  function clearUploadInput(): void {
    if (!uploadInput.current) {
      return;
    }

    uploadInput.current.value = "";
  }

  const handleReject = () => {
    clearUploadInput();
    setSelectedFile(null);
    updateShowPreCheckInfos(false);
    updateIsFinished(false);
    updateIsSuccessful(false);
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem 0" }}>
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label
        htmlFor="file-upload"
        className="k-button k-button-md k-button-rectangle k-button-solid k-button-solid-primary k-rounded-md"
        aria-disabled={showPreCheckInfos}
        style={{
          display: "inline-block",
          cursor: "pointer",
          margin: "0 0.5rem",
        }}
      >
        <FontAwesomeIcon icon={faUpload} /> PDSR-Upload: Select file
      </label>
      <input
        style={{ display: "none" }}
        id="file-upload"
        type="file"
        onChange={changeHandler}
        ref={uploadInput}
      />
      {selectedFile?.name}
      {isFinished && isSuccessful && (
        <div style={{ padding: "1rem 0.5rem" }}>
          <b>Successfully uploaded</b>
        </div>
      )}
      {isFinished && !isSuccessful && (
        <div style={{ padding: "1rem 0.5rem" }}>
          <span>
            <b>Error</b> {errorMessage}
          </span>
        </div>
      )}
      {!isFinished && showPreCheckInfos && (
        <div style={{ padding: "1rem 0.5rem" }}>
          <h4>Changes with this file:</h4>
          <p>
            <span style={{ margin: "0 0.5rem 0 0" }}>
              Neu: <b>{preCheckResponse?.new}</b>
            </span>
            <span style={{ margin: "0 0.5rem 0 0" }}>
              Updated: <b>{preCheckResponse?.updated}</b>
            </span>
            <span>
              Deleted: <b>{preCheckResponse?.removed}</b>
            </span>
          </p>
          <Button onClick={handleReject} style={{ margin: "0 0.5rem 0 0" }}>
            Discard
          </Button>
          <Button onClick={handleAccept} themeColor="primary">
            Save/Upload
          </Button>
        </div>
      )}
    </div>
  );
}
